function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; var ownKeys = Object.keys(source); if (typeof Object.getOwnPropertySymbols === 'function') { ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function (sym) { return Object.getOwnPropertyDescriptor(source, sym).enumerable; })); } ownKeys.forEach(function (key) { _defineProperty(target, key, source[key]); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import gettext from 'js-utils/gettext';
import lazyGettext from 'js-utils/lazy-gettext';
import { ANCHOR_LINK, DIVIDER } from 'eventbrite_design_system/navListItem/constants';
import { getAnalyticsClickHandler } from '@eb/site-analytics';
import { isNumber } from 'lodash';
import { getUserMenuItemsLookup } from './user';
import { ACTION_CREATE_ATTEMPT, ACTION_DISCOVER, ACTION_HELP_ARTICLE, ACTION_LOGIN_ATTEMPT, ACTION_LOGO_HOME, CATEGORY_HEADER, LABEL_HELP_CENTER } from '../constants';
export var isHomepage = function isHomepage(requestPath) {
  return requestPath === '/';
};
/**
 * Generates data needed to pass as `logoInfo` prop to EDS Global Header
 * @param {string} url URL to navigate to when logo is clicked
 * @param {string} requestPath Current request path (for analytics)
 * @param {object} [logoOptions={}] Additional options matching the EDS Global Header `logoInfo` prop
 * @returns {object}
 */

export var getLogoInfo = function getLogoInfo(url, requestPath, logoOptions) {
  if (logoOptions === void 0) {
    logoOptions = {};
  }

  return _objectSpread({
    url: url,
    onClick: getAnalyticsClickHandler({
      requestPath: requestPath,
      action: ACTION_LOGO_HOME,
      category: CATEGORY_HEADER
    })
  }, logoOptions);
};
var knownOrganizerManageUrlPathsRegexp = /^\/(affiliates|facebook-publish|links|manage\/events|myevent|organizations|questions)/;
/**
 * Generates a link for the logo based on the current context.
 *
 * Generally, this helps Music customers get back to their events listing
 * rather than the consumer homepage.
 *
 * @param {string} user         The currently logged-in user
 * @param {string} fallbackUrl  The original URL to fall back on
 * @param {string} requestPath  The current path the user is navigating from
 */

export var getLogoInfoUrl = function getLogoInfoUrl(user, fallbackUrl, requestPath) {
  if (user && user.usesNewPermissions && knownOrganizerManageUrlPathsRegexp.test(requestPath)) {
    return '/organizations/events';
  }

  return fallbackUrl;
};
/**
 * Retrieves list of browse events-related quick links to pass to `quickLinks` prop of EDS Global Header
 * @param {string} serverUrl Base site URL (e.g. "http://www.eventbrite.com")
 * @param {string} ebDomain Current site domain (for Janus checkpoint)
 * @param {string} requestPath Current request path (for analytics)
 * @returns {object[]}
 */

export var getBrowseEventsQuickLinks = function getBrowseEventsQuickLinks(serverUrl, ebDomain, requestPath) {
  return [{
    url: serverUrl + "/d/local/all-events/",
    content: gettext('Browse Events'),
    onClick: getAnalyticsClickHandler({
      requestPath: requestPath,
      action: ACTION_DISCOVER,
      category: CATEGORY_HEADER
    }, {
      checkpointName: 'discover_header_link_clicked',
      domain: ebDomain
    })
  }];
};
/**
 * Gets the info for displaying create event CTA to be passed to `callToActionInfo` prop of EDS Global Header
 * @param {string} serverUrl Base site URL (e.g. "http://www.eventbrite.com")
 * @param {string} ebDomain Current site domain (for Janus checkpoint)
 * @param {string} requestPath Current request path (for analytics)
 * @param {boolean} isAuthenticated Whether or not the user is authenticated
 * @param {boolean} canCreateEvents Whether or not the user can create events
 * @param {React.ReactNode} [content='Create Event'] The content of the CTA
 * @param {object} [dimensions={}] Additional GA dimensions for when the CTA is clicked
 * @returns {object}
 */

export var getCreateEventCTA = function getCreateEventCTA(serverUrl, ebDomain, requestPath, isAuthenticated, canCreateEvents, content, dimensions) {
  if (content === void 0) {
    content = lazyGettext('Create Event');
  }

  if (dimensions === void 0) {
    dimensions = {};
  }

  var callToAction;

  if (canCreateEvents || isHomepage(requestPath)) {
    var dataAttributes = {
      'data-automation': 'global-nav-create',
      onClick: getAnalyticsClickHandler({
        requestPath: requestPath,
        action: ACTION_CREATE_ATTEMPT,
        category: CATEGORY_HEADER,
        dimensions: dimensions
      }, {
        checkpointName: 'hiw_global_header_cta_clicked',
        pageWillChange: isAuthenticated,
        domain: ebDomain
      })
    };
    callToAction = _objectSpread({
      url: serverUrl + "/create/",
      content: content
    }, dataAttributes);
  }

  return callToAction;
};
/**
 * @typedef SearchSuggestion
 * @property {string} value
 * @property {string} content
 */

/**
 * Gets the search information passed to the `searchInfo` prop of EDS Global Header
 * @param {string} serverUrl Base site URL (e.g. "http://www.eventbrite.com")
 * @param {string} query Current search query
 * @param {SearchSuggestion[]} suggestions Search suggestions for auto-complete
 * @param {boolean} hideSearchBar Whether or not to hide the search bar
 * @returns {object}
 */

export var getSearchInfo = function getSearchInfo(serverUrl, query, suggestions, hideSearchBar) {
  var searchInfo;

  if (!hideSearchBar) {
    searchInfo = {
      formAction: serverUrl + "/d/",
      label: gettext('Search for events'),
      query: query,
      suggestions: suggestions
    };
  }

  return searchInfo;
};
/**
 * Gets list of links usually passed as `nonUserQuickLinks` prop to EDS Global Header
 * @param {string} requestPath Current request path (for analytics)
 * @param {boolean} isAuthenticated Whether or not the user is authenticated
 * @param {string} loginUrl URL to the sign-in page
 * @returns object[]
 */

export var getNonUserQuickLinks = function getNonUserQuickLinks(requestPath, isAuthenticated, loginUrl) {
  if (isAuthenticated) {
    return null;
  }

  return [{
    url: loginUrl,
    content: gettext('Sign In'),
    onClick: getAnalyticsClickHandler({
      requestPath: requestPath,
      action: ACTION_LOGIN_ATTEMPT,
      category: CATEGORY_HEADER
    })
  }];
};
/**
 * @typedef {object} UrlInfo
 * @property {string} url Help URL
 * @property {string} label Display label
 * @property {string} gaLabel Google Analytics label
 */

/**
 * Retrieves the help menu info typically passed to the `additionalMenus` prop of the EDS Global Header
 * @param {object} helpUrls
 * @param {string} helpUrls.home
 * @param {UrlInfo[]} helpUrls.suggested
 * @param {string} requestPath Current request path (for analytics)
 * @returns object
 */

export var getHelpMenu = function getHelpMenu(_ref, requestPath) {
  var home = _ref.home,
      suggested = _ref.suggested;
  var suggestedHelpMenuItems = suggested.map(function (_ref2) {
    var url = _ref2.url,
        label = _ref2.label,
        gaLabel = _ref2.gaLabel;
    return {
      type: ANCHOR_LINK,
      path: url,
      content: label,
      onClick: getAnalyticsClickHandler({
        requestPath: requestPath,
        action: ACTION_HELP_ARTICLE,
        category: CATEGORY_HEADER,
        label: gaLabel
      })
    };
  });
  var navItems = [].concat(suggestedHelpMenuItems, [{
    type: DIVIDER
  }, {
    type: ANCHOR_LINK,
    path: home,
    content: gettext('Help Center'),
    target: '_blank',
    rel: 'noopener noreferrer',
    onClick: getAnalyticsClickHandler({
      requestPath: requestPath,
      action: ACTION_HELP_ARTICLE,
      category: CATEGORY_HEADER,
      label: LABEL_HELP_CENTER
    })
  }]);
  return {
    label: gettext('Help'),
    fallbackUrl: home,
    navItems: navItems
  };
};
/**
 * Gets info to generate Organizer menu item usually passed `additionalMenus` prop of EDS Global Header
 * @param {object} organizeUrls
 * @param {UrlInfo} organizeUrls.home
 * @param {UrlInfo[]} organizeUrls.suggested
 * @param {string} requestPath Current request path (for analytics)
 * @returns {object}
 */

export var getOrganizeMenu = function getOrganizeMenu(_ref3, requestPath) {
  var homeUrl = _ref3.home.url,
      suggested = _ref3.suggested;
  var suggestedOrganizeMenuItems = suggested.map(function (_ref4) {
    var url = _ref4.url,
        label = _ref4.label,
        gaLabel = _ref4.gaLabel;
    return {
      type: ANCHOR_LINK,
      path: url,
      content: label,
      onClick: getAnalyticsClickHandler({
        requestPath: requestPath,
        action: '',
        category: CATEGORY_HEADER,
        label: gaLabel
      })
    };
  });
  var navItems = [].concat(suggestedOrganizeMenuItems);
  return {
    label: gettext('Organize'),
    fallbackUrl: homeUrl,
    navItems: navItems
  };
};
/**
 * Retrieves the list of staff-related user menu items given the specified configurations
 * @param {object} options
 * @param {object} options.userMenuItemsLookup Lookup of all user menu items
 * @param {boolean} options.isStaff Whether or not the account is a staff account
 * @param {boolean} options.hasTikibar Whether or not the account has access to Tikibar
 * @returns {object[]}
 */

export var getStaffUserMenuItems = function getStaffUserMenuItems(_ref5) {
  var userMenuItemsLookup = _ref5.userMenuItemsLookup,
      isStaff = _ref5.isStaff,
      hasTikibar = _ref5.hasTikibar;

  if (!isStaff) {
    return [];
  }

  var admin = userMenuItemsLookup.admin,
      tikibar = userMenuItemsLookup.tikibar,
      featureFlags = userMenuItemsLookup.featureFlags,
      experiments = userMenuItemsLookup.experiments;
  var menuItems = [admin];

  if (hasTikibar) {
    menuItems.push(tikibar);
  }

  menuItems.push(featureFlags, experiments, {
    type: DIVIDER
  });
  return menuItems;
};
/**
 * Retrieves the list of attendee-related user menu items given the specified configurations
 * @param {object} options
 * @param {object} options.userMenuItemsLookup Lookup of all user menu items
 * @param {number} [options.numFollowing] How many accounts the user is following
 * @param {boolean} options.canUseCollections Whether or not the user has access to collections
 * @param {boolean} options.showBrowseEvents Whether or not to show the "Browse events" menu item
 * @param {boolean} options.canUseAttendeeNewsletter Whether or not the user has access to the attendee newsletter
 * @param {boolean} options.canUseInterests Whether or not the user has access to Interests
 * @returns {object[]}
 */

export var getAttendeeUserMenuItems = function getAttendeeUserMenuItems(_ref6) {
  var userMenuItemsLookup = _ref6.userMenuItemsLookup,
      numFollowing = _ref6.numFollowing,
      canUseCollections = _ref6.canUseCollections,
      showBrowseEvents = _ref6.showBrowseEvents,
      canUseAttendeeNewsletter = _ref6.canUseAttendeeNewsletter,
      canUseInterests = _ref6.canUseInterests;
  var browseEvents = userMenuItemsLookup.browseEvents,
      tickets = userMenuItemsLookup.tickets,
      saved = userMenuItemsLookup.saved,
      following = userMenuItemsLookup.following,
      collections = userMenuItemsLookup.collections,
      attendeeNewsletter = userMenuItemsLookup.attendeeNewsletter,
      interests = userMenuItemsLookup.interests;
  var attendeeItems = [];

  if (showBrowseEvents) {
    attendeeItems.push(browseEvents, {
      type: DIVIDER
    });
  }

  attendeeItems.push(tickets, saved);
  /* While ENABLE_DESTINATION_PROFILE is ramping up if numFollowing
  is undefined or null, that means that the user is not enrolled and
  so we should not display any following related links. */

  if (isNumber(numFollowing)) {
    attendeeItems.push(following);
  }

  if (canUseCollections) {
    attendeeItems.push(collections);
  }

  if (canUseAttendeeNewsletter) {
    attendeeItems.push(attendeeNewsletter);
  }

  if (canUseInterests) {
    attendeeItems.push(interests);
  }

  return attendeeItems;
};

var _getOrganizerMenuItems = function _getOrganizerMenuItems(_ref7) {
  var userMenuItemsLookup = _ref7.userMenuItemsLookup,
      canInvite = _ref7.canInvite,
      hasEvents = _ref7.hasEvents,
      hasOrganizers = _ref7.hasOrganizers,
      isAttendee = _ref7.isAttendee,
      canUseSponsorships = _ref7.canUseSponsorships,
      canUseStudio = _ref7.canUseStudio;

  if (isAttendee) {
    return [];
  }

  var manageEvents = userMenuItemsLookup.manageEvents,
      organizerProfile = userMenuItemsLookup.organizerProfile,
      contacts = userMenuItemsLookup.contacts,
      studio = userMenuItemsLookup.studio,
      sponsorships = userMenuItemsLookup.sponsorships;
  var menuItems = [{
    type: DIVIDER
  }, manageEvents];

  if (hasOrganizers) {
    menuItems.push(organizerProfile);
  }

  if (hasEvents || canInvite) {
    menuItems.push(contacts);
  }

  if (canUseStudio) {
    menuItems.push(studio);
  }

  if (canUseSponsorships) {
    menuItems.push(sponsorships);
  }

  return menuItems;
};

export var _getCreateEventMenuItems = function _getCreateEventMenuItems(_ref8) {
  var userMenuItemsLookup = _ref8.userMenuItemsLookup,
      canCreateEvents = _ref8.canCreateEvents;

  if (!canCreateEvents) {
    return [];
  }

  return [userMenuItemsLookup.create];
};

var _getUserContentMenuItems = function _getUserContentMenuItems(userMenuItemsLookup) {
  return [userMenuItemsLookup.accountSettingsWithIcon, {
    type: DIVIDER
  }];
};

var _getUserMenuItems = function _getUserMenuItems(_ref9) {
  var adminServerUrl = _ref9.adminServerUrl,
      canCreateEvents = _ref9.canCreateEvents,
      canInvite = _ref9.canInvite,
      canUseAttendeeNewsletter = _ref9.canUseAttendeeNewsletter,
      canUseSponsorships = _ref9.canUseSponsorships,
      canUseStudio = _ref9.canUseStudio,
      canUseInterests = _ref9.canUseInterests,
      canUseCollections = _ref9.canUseCollections,
      hasEvents = _ref9.hasEvents,
      hasOrganizers = _ref9.hasOrganizers,
      hasTikibar = _ref9.hasTikibar,
      isAttendee = _ref9.isAttendee,
      isStaff = _ref9.isStaff,
      logoutUrl = _ref9.logoutUrl,
      numOrders = _ref9.numOrders,
      numSavedEvents = _ref9.numSavedEvents,
      numFollowing = _ref9.numFollowing,
      publicId = _ref9.publicId,
      requestPath = _ref9.requestPath,
      serverUrl = _ref9.serverUrl,
      accountSettingsPath = _ref9.accountSettingsPath,
      showBrowseEvents = _ref9.showBrowseEvents,
      email = _ref9.email,
      fullName = _ref9.fullName,
      userImageUrl = _ref9.userImageUrl,
      isManageOverviewActive = _ref9.isManageOverviewActive;
  var userMenuItemsLookup = getUserMenuItemsLookup({
    adminServerUrl: adminServerUrl,
    logoutUrl: logoutUrl,
    numOrders: numOrders,
    numSavedEvents: numSavedEvents,
    numFollowing: numFollowing,
    publicId: publicId,
    requestPath: requestPath,
    serverUrl: serverUrl,
    accountSettingsPath: accountSettingsPath,
    email: email,
    fullName: fullName,
    userImageUrl: userImageUrl,
    isManageOverviewActive: isManageOverviewActive
  });

  var userMenuItems = _getUserContentMenuItems(userMenuItemsLookup);

  var staffUserMenuItems = getStaffUserMenuItems({
    userMenuItemsLookup: userMenuItemsLookup,
    hasTikibar: hasTikibar,
    isStaff: isStaff
  });
  var attendeeMenuItems = getAttendeeUserMenuItems({
    userMenuItemsLookup: userMenuItemsLookup,
    numFollowing: numFollowing,
    canUseCollections: canUseCollections,
    showBrowseEvents: showBrowseEvents,
    canUseAttendeeNewsletter: canUseAttendeeNewsletter,
    canUseInterests: canUseInterests
  });

  var organizerMenuItems = _getOrganizerMenuItems({
    userMenuItemsLookup: userMenuItemsLookup,
    canInvite: canInvite,
    hasEvents: hasEvents,
    hasOrganizers: hasOrganizers,
    isAttendee: isAttendee,
    canUseSponsorships: canUseSponsorships,
    canUseStudio: canUseStudio
  });

  var createEventMenuItems = _getCreateEventMenuItems({
    userMenuItemsLookup: userMenuItemsLookup,
    canCreateEvents: canCreateEvents
  });

  return [].concat(userMenuItems, staffUserMenuItems, attendeeMenuItems, organizerMenuItems, [{
    type: DIVIDER
  }, userMenuItemsLookup.accountSettings], createEventMenuItems, [userMenuItemsLookup.logout]);
};
/**
 * Get info for the user menu typically passed to the `userMenuInfo` prop of EDS Global Header
 * @param {object} options
 * @param {string} options.serverUrl Base site URL (e.g. "http://www.eventbrite.com")
 * @param {string} options.adminServerUrl Base Admin site URL (e.gettext. "http://admin.eventbrite.com")
 * @param {string} options.logoutUrl URL to logout page
 * @param {string} options.requestPath Current request path (for analytics)
 * @param {object} options.user User object
 * @param {boolean} options.showBrowseEvents Whether or not to include Browse Events menu item
 * @param {boolean} options.isNewAccountSettingsActive Whether or not the new account settings page is active
 * @param {boolean} options.isManageOverviewActive Whether or not the new Manage Overview page is active
 * @param {string} [options.iconColor] Color of the user icon
 */


export var getUserMenuInfo = function getUserMenuInfo(_ref10) {
  var serverUrl = _ref10.serverUrl,
      adminServerUrl = _ref10.adminServerUrl,
      logoutUrl = _ref10.logoutUrl,
      requestPath = _ref10.requestPath,
      _ref10$user = _ref10.user,
      canCreateEvents = _ref10$user.canCreateEvents,
      canInvite = _ref10$user.canInvite,
      canUseAttendeeNewsletter = _ref10$user.canUseAttendeeNewsletter,
      canUseSponsorships = _ref10$user.canUseSponsorships,
      canUseStudio = _ref10$user.canUseStudio,
      canUseInterests = _ref10$user.canUseInterests,
      canUseCollections = _ref10$user.canUseCollections,
      firstName = _ref10$user.firstName,
      hasEvents = _ref10$user.hasEvents,
      hasOrganizers = _ref10$user.hasOrganizers,
      hasTikibar = _ref10$user.hasTikibar,
      isAttendee = _ref10$user.isAttendee,
      isAuthenticated = _ref10$user.isAuthenticated,
      isStaff = _ref10$user.isStaff,
      numOrders = _ref10$user.numOrders,
      numSavedEvents = _ref10$user.numSavedEvents,
      numFollowing = _ref10$user.numFollowing,
      publicId = _ref10$user.publicId,
      thumbnailUrl = _ref10$user.thumbnailUrl,
      fullName = _ref10$user.fullName,
      email = _ref10$user.email,
      showBrowseEvents = _ref10.showBrowseEvents,
      isNewAccountSettingsActive = _ref10.isNewAccountSettingsActive,
      isManageOverviewActive = _ref10.isManageOverviewActive,
      _ref10$iconColor = _ref10.iconColor,
      iconColor = _ref10$iconColor === void 0 ? undefined : _ref10$iconColor;

  if (!isAuthenticated) {
    return null;
  }

  var accountSettingsPath = isNewAccountSettingsActive ? 'account-settings' : 'account';
  return {
    iconColor: iconColor,
    label: firstName,
    fallbackUrl: serverUrl + "/" + accountSettingsPath + "/",
    imageUrl: thumbnailUrl,
    navItems: _getUserMenuItems({
      adminServerUrl: adminServerUrl,
      canCreateEvents: canCreateEvents,
      canInvite: canInvite,
      canUseAttendeeNewsletter: canUseAttendeeNewsletter,
      canUseSponsorships: canUseSponsorships,
      canUseStudio: canUseStudio,
      canUseInterests: canUseInterests,
      canUseCollections: canUseCollections,
      hasEvents: hasEvents,
      hasOrganizers: hasOrganizers,
      hasTikibar: hasTikibar,
      isAttendee: isAttendee,
      isStaff: isStaff,
      logoutUrl: logoutUrl,
      numOrders: numOrders,
      numSavedEvents: numSavedEvents,
      numFollowing: numFollowing,
      publicId: publicId,
      requestPath: requestPath,
      serverUrl: serverUrl,
      accountSettingsPath: accountSettingsPath,
      showBrowseEvents: showBrowseEvents,
      fullName: fullName,
      email: email,
      userImageUrl: thumbnailUrl,
      isManageOverviewActive: isManageOverviewActive
    })
  };
};